import React, { lazy, Suspense } from 'react';
import { isSfxTraceOn } from 'utils/sfxTrace';

/**
 * The SFX Lab's front door, and the only thing outside this folder that may
 * import it.
 *
 * The lab is a permanent workshop tool, not a shipped feature: it exists so a
 * future sound pass can re-open the tweak panel and the cue trace instead of
 * rebuilding them. Keeping it in the tree that way is only safe if a player
 * can never reach it, so there are two independent locks.
 *
 * ── LOCK 1: the build ────────────────────────────────────────────────────
 * `REACT_APP_ENVIRONMENT` is written into .env by deploy-web-app.yml from the
 * deploy stage. This is an ALLOWLIST of the lower environments, deliberately,
 * because the denylist version of it is a trap: production deploys under TWO
 * names — `production` and `live` (deploy-web-app.yml:163) — so `!== production`
 * would have shipped the lab to real players on every `live` build. Written
 * this way the unsafe cases all fail closed: `production`, `live`, a variable
 * nobody set, and any stage name invented after this line was written are all
 * simply not on the list.
 *
 * ── LOCK 2: the operator ─────────────────────────────────────────────────
 * Even where it is allowed, it stays invisible until someone sets
 * `localStorage.hijack_sfxlab = '1'` and reloads. Nobody arrives at that by
 * accident.
 *
 * The lab is also loaded through `lazy`, so its ~650 lines and stylesheet sit
 * in their own chunk that is fetched only when both locks open — they are not
 * part of the bundle every player downloads.
 *
 * To bring it back for a future sound pass: set the localStorage key on a
 * lower environment. Nothing needs to be uncommented.
 */
const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
const LAB_AVAILABLE =
  // A local dev server or a jest run — never a deploy, because `react-scripts
  // build` always sets this to production. Keeps the lab working locally even
  // though .env.local leaves the stage blank.
  process.env.NODE_ENV !== 'production' ||
  // A deployed lower environment (develop*, test*, staging).
  /^(develop|test|staging)/.test(stage);

const SfxLab = LAB_AVAILABLE ? lazy(() => import('./SfxLab')) : null;

export const SfxLabMount = () => {
  if (!SfxLab || !isSfxTraceOn()) return null;
  return (
    <Suspense fallback={null}>
      <SfxLab />
    </Suspense>
  );
};

export default SfxLabMount;
